import React, { useEffect, useState } from "react";
import { message, Popconfirm } from "antd";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { deleteFormula, getFormulas } from "./formulaBuilder.api";
import type { FormulaItem } from "./formulaBuilder.types";
import { GRAIN_LABELS } from "./formulaBuilder.utils";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

type Props = {
  onAdd: () => void;
  onEdit: (formulaId: string) => void;
};

const FormulaBuilderList: React.FC<Props> = ({ onAdd, onEdit }) => {
  const { t } = useTranslation("formulaBuilder");
  const { id: projectId } = useParams();

  const [formulas, setFormulas] = useState<FormulaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const limit = 10;

  const fetchFormulas = async (currentPage = page) => {
    if (!projectId) return;

    setLoading(true);

    try {
      const skip = (currentPage - 1) * limit;
      const response = await getFormulas(projectId, { skip, limit });

      if (response.type === "success") {
        setFormulas(response.data.formulas ?? []);
        setTotal(response.data.total ?? 0);
      } else {
        message.error(t("list.loadFailed"));
      }
    } catch (error) {
      message.error(t("list.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormulas(page);
  }, [page, projectId]);

  const handleDelete = async (formulaId: string) => {
    if (!projectId) return;

    try {
      const response = await deleteFormula(projectId, formulaId);

      if (response.type === "success") {
        message.success(t("list.deleteSuccess"));
        fetchFormulas(page);
      } else {
        message.error(t("list.deleteFailed"));
      }
    } catch (error) {
      message.error(t("list.deleteFailed"));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="formula-builder-section">
      <div className="formula-builder-header">
        <div>
          <h1>{t("list.title")}</h1>
          <p>
            {t("list.description")}
          </p>
        </div>

        <button type="button" className="add-formula-btn" onClick={onAdd}>
          + {t("list.addFormula")}
        </button>
      </div>

      <div className="formula-table-wrap">
        <table className="formula-table">
          <thead>
            <tr>
              <th>{t("list.table.formula")}</th>
              <th>{t("list.table.alignData")}</th>
              <th>{t("list.table.variablesUsed")}</th>
              <th>{t("list.table.lastResult")}</th>
              <th>{t("list.table.status")}</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={4}>{t("list.loading")}</td>
              </tr>
            )}

            {!loading && formulas.length === 0 && (
              <tr>
                <td colSpan={4}>{t("list.noFormulasFound")}</td>
              </tr>
            )}

            {!loading &&
              formulas.map((formula) => (
                <tr key={formula.id}>
                  <td>{formula.name}</td>
                  <td>{GRAIN_LABELS[formula.grain] ?? formula.grain}</td>
                  <td>{formula.variables_used?.join(", ") || "-"}</td>
                  <td>{formula.last_result ?? "-"}</td>
                  <td>
                    <span
                      className={`formula-status-badge formula-status-${formula.status}`}
                    >
                      {formula.status}
                    </span>
                  </td>
                  <td className="formula-actions">
                    <EditOutlined
                      style={{
                        fill: "#A072EB",
                        color: "#A072EB",
                        cursor: "pointer",
                      }}
                      type="button"
                      className="formula-icon-btn"
                      onClick={() => onEdit(formula.id)}
                    ></EditOutlined>

                    <Popconfirm
                      title={t("list.deleteConfirmTitle")}
                      okText={t("list.deleteConfirmOk")}
                      cancelText={t("list.deleteConfirmCancel")}
                      onConfirm={() => handleDelete(formula.id)}
                    >
                      <DeleteOutlined
                        style={{
                          fill: "#A072EB",
                          color: "#A072EB",
                          cursor: "pointer",
                        }}
                        type="button"
                        className="formula-icon-btn"
                      ></DeleteOutlined>
                    </Popconfirm>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="formula-pagination">
        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          {"<"}
        </button>

        {Array.from({ length: totalPages }).map((_, index) => {
          const pageNumber = index + 1;

          return (
            <button
              key={pageNumber}
              className={page === pageNumber ? "active" : ""}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </button>
          );
        })}

        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {">"}
        </button>
      </div>
    </div>
  );
};

export default FormulaBuilderList;
